import { ApiErrorCode, ApiResponse, withApiErrorHandler } from '@/modules/common/api/errors';
import { CreatedEntity } from '@/modules/common/types/created-entity';
import { axiosInstance } from '@/utils/axios';

import { mapPaymentDetailsListFromResponse } from '../mappers/payment-details.mapper';
import { PaymentDetailsEntry } from '../types/payment-details';
import { SavePaymentDetailsRequest } from '../types/request/payment-details-request';
import { PaymentDetailsResponse } from '../types/response/payment-details-response';

/**
 * Fetches all payment-details entries for the settings table.
 * No pagination — entry counts are small (soft-capped per org), same pattern as seniorities.
 */
export const getPaymentDetails = async (): Promise<PaymentDetailsEntry[]> => {
  const endpoint = '/api/payment-details';

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.PAYMENT_DETAILS_LOAD_FAILED,
    () => axiosInstance.get<ApiResponse<PaymentDetailsResponse[]>>(endpoint)
  );

  return mapPaymentDetailsListFromResponse(response);
};

/**
 * Fetches short payment-details entries for selectors (invoice generation).
 * No pagination — same item shape as the full list; `details` is included for previews/tooltips.
 */
export const getShortPaymentDetails = async (): Promise<PaymentDetailsEntry[]> => {
  const endpoint = '/api/payment-details/short';

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.PAYMENT_DETAILS_OPTIONS_LOAD_FAILED,
    () => axiosInstance.get<ApiResponse<PaymentDetailsResponse[]>>(endpoint)
  );

  return mapPaymentDetailsListFromResponse(response);
};

export const createPaymentDetails = async (
  data: SavePaymentDetailsRequest
): Promise<CreatedEntity> => {
  const endpoint = '/api/payment-details';

  return withApiErrorHandler(endpoint, ApiErrorCode.PAYMENT_DETAILS_CREATE_FAILED, () =>
    axiosInstance.post<ApiResponse<CreatedEntity>>(endpoint, data)
  );
};

export const updatePaymentDetails = async ({
  id,
  ...data
}: SavePaymentDetailsRequest & { id: number }): Promise<void> => {
  const endpoint = `/api/payment-details/${id}`;

  await withApiErrorHandler(endpoint, ApiErrorCode.PAYMENT_DETAILS_UPDATE_FAILED, () =>
    axiosInstance.patch<ApiResponse<PaymentDetailsResponse>>(endpoint, data)
  );
};

export const deletePaymentDetails = async (id: number): Promise<void> => {
  const endpoint = `/api/payment-details/${id}`;

  await withApiErrorHandler(endpoint, ApiErrorCode.PAYMENT_DETAILS_DELETE_FAILED, () =>
    axiosInstance.delete<ApiResponse<void>>(endpoint)
  );
};
